import { Fragment, useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { canWorkSales, canManageSalesOrders } from "@/lib/permissions";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { ListSkeleton } from "@/components/base/ListSkeleton";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { money, fmtDate, variantLabel } from "@/lib/format";
import { getActiveCurrencyCode } from "@/lib/currency";
import * as salesApi from "@/lib/api/sales-orders";
import { ALL_SO_STATUSES, FULFILLABLE_STATUSES, orderTotal, paymentStatusLabel, paymentStatusTone, soStatusLabel, shouldPromoteAllocateCta } from "@/lib/sales-orders";
import { buildProvenance, provenanceStoreLabel } from "@/lib/order-provenance";
import { downloadCsv, toCsv } from "@/lib/csv";
import Pagination from "@/components/base/Pagination";
import { useCreateDeepLink } from "@/hooks/useCreateDeepLink";
import { firstRelation } from "@/lib/relations";
import { historicalProductName, historicalSku, historicalVariantOptions, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { summarizeSalesOrderItems } from "@/lib/sales-order-list-summary";
import { indexSalesOrderListAfterSalesBadges, SALES_ORDER_LIST_AFTER_SALES_EXCEPTION_TYPES, type SalesOrderListInboundEvent, type SalesOrderListOperationalIssue } from "@/lib/sales-order-list-after-sales";
import SalesOrderCard from "@/pages/sales-orders/components/SalesOrderCard";
import { SalesOrderListAfterSalesBadge } from "@/pages/sales-orders/components/SalesOrderListAfterSalesBadge";
import { SalesOrderItemSummaryView } from "@/pages/sales-orders/components/SalesOrderItemSummaryView";
import { FulfilOrderModal } from "@/pages/sales-orders/components/FulfilOrderModal";
import { PrintDocumentsMenu } from "@/pages/sales-orders/components/PrintDocumentsMenu";
import { BulkActionBar } from "@/components/feature/BulkActionBar";
import { useBulkSelection } from "@/components/feature/useBulkSelection";
import { canPrintOrderDocuments, type OrderDocumentKind } from "@/lib/order-documents";
import { printSalesOrderDocuments } from "@/lib/api/order-documents";
import {
  balanceKey,
  buildFulfilmentRequest,
  fulfilmentConfirmDisabled,
  seedLineFulfilmentLocations,
} from "@/lib/fulfilment-location";

interface SoRow {
  id: string;
  order_number: string;
  customer_id: string | null;
  location_id: string | null;
  channel_id?: string | null;
  source: string;
  status: string;
  payment_status: string | null;
  order_date: string | null;
  total: number;
  customers?: { name: string } | null;
  locations?: { name: string } | null;
  connectors?: { name: string; provider: string | null } | null;
}

interface SoItem extends CatalogueHistoryFields {
  id: string;
  sales_order_id: string;
  product_id: string | null;
  variant_id: string | null;
  qty_ordered: number;
  qty_allocated: number;
  qty_fulfilled: number;
  unit_price: number;
  is_bundle: boolean | null;
  bundle_parent_item_id: string | null;
  products?: { name: string; sku: string } | null;
  variants?: { sku: string | null; option_values: Record<string, string> | null } | null;
}

interface Option { id: string; name: string; }
interface ProductOption { id: string; name: string; sku: string; selling_price: number; product_type: string | null; }

interface DraftLine { uid: string; product_id: string; variant_id: string | null; qty: number; unit_price: string; is_bundle?: boolean; }
interface VariantOption { id: string; product_id: string; sku: string | null; option_values: Record<string, string> | null; selling_price: number; }
interface BundleCheck { id: string; name: string; product_type: string | null; sku: string | null; }

const sanitize = (q: string) => q.replace(/[,%_()]/g, "").trim();

const inputCls =
  "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";
const paymentBadgeCls: Record<string, string> = {
  paid: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  refunded: "bg-slate-100 text-slate-600",
  unpaid: "bg-slate-100 text-slate-600",
};

export default function SalesOrders() {
  const { org, role } = useAuth();
  const canCreate = canManageSalesOrders(role);
  const canFulfilRole = canWorkSales(role);
  const canAllocateRole = canManageSalesOrders(role);
  const canPrint = canPrintOrderDocuments(role);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [fulfilSo, setFulfilSo] = useState<SoRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [customerId, setCustomerId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [fulfilQty, setFulfilQty] = useState<Record<string, number>>({});
  const [fulfilLoc, setFulfilLoc] = useState<Record<string, string>>({});
  const [fulfilIdemKey, setFulfilIdemKey] = useState("");
  const [createIdemKey, setCreateIdemKey] = useState("");
  const [priority, setPriority] = useState("normal");
  const [customerReference, setCustomerReference] = useState("");
  const [notes, setNotes] = useState("");
  const [discount, setDiscount] = useState("");
  const [tax, setTax] = useState("");
  const [shipping, setShipping] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [printError, setPrintError] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["sales-orders", org?.id, search, statusFilter, page, pageSize],
    enabled: !!org?.id,
    queryFn: async () => {
      let q = supabase
        .from("sales_orders")
        .select("id, order_number, customer_id, location_id, channel_id, source, status, payment_status, order_date, total, customers(name), locations(name), connectors(name, provider)", { count: "exact" })
        .eq("org_id", org!.id);
      const term = sanitize(search);
      if (term) {
        const ilike = `%${term}%`;
        const { data: matchedCustomers } = await supabase
          .from("customers")
          .select("id")
          .eq("org_id", org!.id)
          .ilike("name", ilike)
          .limit(100);
        const customerIds = ((matchedCustomers ?? []) as { id: string }[]).map((c) => c.id);
        if (customerIds.length) {
          q = q.or(`order_number.ilike.${ilike},customer_id.in.(${customerIds.join(",")})`);
        } else {
          q = q.ilike("order_number", ilike);
        }
      }
      if (statusFilter) q = q.eq("status", statusFilter);
      q = q.order("created_at", { ascending: false }).range(page * pageSize, page * pageSize + pageSize - 1);
      const sosRes = await q;
      const sos = (sosRes.data ?? []).map((row) => ({
        ...row,
        customers: firstRelation(row.customers),
        locations: firstRelation(row.locations),
        connectors: firstRelation(row.connectors),
      })) as SoRow[];
      const soIds = sos.map((s) => s.id);
      const [items, customers, locations, products, variants, externalIds, inboundEvents, afterSalesIssues] = await Promise.all([
        soIds.length
          ? supabase.from("sales_order_items").select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name, sku, variant_attributes, id, sales_order_id, product_id, variant_id, qty_ordered, qty_allocated, qty_fulfilled, qty_cancelled, unit_price, is_bundle, bundle_parent_item_id, products(name, sku), variants(sku, option_values)").eq("org_id", org!.id).in("sales_order_id", soIds)
          : Promise.resolve({ data: [] }),
        supabase.from("customers").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
        supabase.from("products").select("id, name, sku, selling_price, product_type").eq("org_id", org!.id).neq("status", "archived").order("name"),
        supabase.from("variants").select("id, product_id, sku, option_values, selling_price").eq("org_id", org!.id).neq("status", "archived").order("position"),
        soIds.length
          ? supabase.from("external_identifiers").select("entity_id, external_id").eq("org_id", org!.id).eq("entity_type", "sales_order").in("entity_id", soIds)
          : Promise.resolve({ data: [] }),
        soIds.length
          ? supabase.from("marketplace_inbound_requests").select("sales_order_id, connector_id, request_kind, provider_request_id, initiator, reason, request_state, provider_order_cancel_state, provider_payment_status, provider_fulfillment_status").eq("org_id", org!.id).in("sales_order_id", soIds)
          : Promise.resolve({ data: [] }),
        supabase.from("integration_exceptions").select("entity_ref, exception_type, status, connector_id, external_data").eq("org_id", org!.id).in("exception_type", [...SALES_ORDER_LIST_AFTER_SALES_EXCEPTION_TYPES]).in("status", ["open", "acknowledged"]),
      ]);
      return {
        sos,
        total: sosRes.count ?? 0,
        items: (items.data ?? []).map((row) => ({
          ...row,
          products: firstRelation(row.products),
          variants: firstRelation(row.variants),
        })) as SoItem[],
        customers: (customers.data ?? []) as Option[],
        locations: (locations.data ?? []) as Option[],
        products: (products.data ?? []) as ProductOption[],
        variants: (variants.data ?? []) as VariantOption[],
        externalIds: (externalIds.data ?? []) as { entity_id: string; external_id: string }[],
        afterSalesBadges: indexSalesOrderListAfterSalesBadges({
          inboundEvents: (inboundEvents.data ?? []) as SalesOrderListInboundEvent[],
          operationalIssues: ((afterSalesIssues.data ?? []) as Array<SalesOrderListOperationalIssue & { external_data?: { sales_order_id?: string } | null }>)
            .map((row) => ({
              ...row,
              entity_ref: row.external_data?.sales_order_id || row.entity_ref,
            }))
            .filter((row) => !!row.entity_ref && soIds.includes(row.entity_ref)),
          orders: sos.map((so) => ({
            id: so.id,
            status: so.status,
            paymentStatus: so.payment_status,
            provider: so.connectors?.provider ?? so.source,
          })),
        }),
      };
    },
  });

  const itemsBySo = (data?.items ?? []).reduce<Record<string, SoItem[]>>((acc, it) => {
    (acc[it.sales_order_id] = acc[it.sales_order_id] ?? []).push(it);
    return acc;
  }, {});

  const bulk = useBulkSelection(data?.sos ?? []);

  const { data: fulfilAvailability, isSuccess: fulfilAvailabilityReady } = useQuery({
    queryKey: ["so-list-fulfil-availability", org?.id, fulfilSo?.id],
    enabled: !!org?.id && !!fulfilSo?.id,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async () => {
      const items = (itemsBySo[fulfilSo!.id] ?? []).filter((it) => !it.is_bundle);
      const productIds = [...new Set(items.map((it) => it.product_id))];
      const bals = productIds.length
        ? await supabase
            .from("inventory_balances")
            .select("product_id, variant_id, location_id, available")
            .eq("org_id", org!.id)
            .in("product_id", productIds)
        : { data: [] as { product_id: string; variant_id: string | null; location_id: string; available: number }[], error: null };
      if (bals.error) throw new Error(bals.error.message);
      const map: Record<string, { available: number }> = {};
      for (const b of bals.data ?? []) {
        map[balanceKey(b.product_id, b.variant_id, b.location_id)] = { available: Number(b.available ?? 0) };
      }
      return { map, locations: data?.locations ?? [] };
    },
  });

  const externalIdBySo = (data?.externalIds ?? []).reduce<Record<string, string>>((acc, ei) => {
    if (ei.external_id) acc[ei.entity_id] = ei.external_id;
    return acc;
  }, {});

  const addLine = () => setLines((l) => [...l, { uid: crypto.randomUUID(), product_id: "", variant_id: null, qty: 1, unit_price: "" }]);
  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setLines((l) => l.map((line, i) => (i === idx ? { ...line, ...patch } : line)));
  const removeLine = (idx: number) => setLines((l) => l.filter((_, i) => i !== idx));
  const createTotal = lines.reduce((sum, l) => sum + (Number(l.qty) || 0) * (Number(l.unit_price) || 0), 0);
  const grandTotal = orderTotal(createTotal, Number(tax) || 0, Number(shipping) || 0, Number(discount) || 0);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!customerId || !locationId || lines.length === 0) {
      setError("Select a customer, location, and at least one line item.");
      return;
    }
    if (lines.some((l) => !l.product_id || Number(l.qty) <= 0)) {
      setError("Every line needs a product and a positive quantity.");
      return;
    }
    // Canonical variant rule: a variable product (more than one sellable variant)
    // requires an explicit variant selection; simple products auto-resolve to
    // their single variant server-side, so the user never sees the detail.
    if (lines.some((l) => {
      const vs = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
      return vs.length > 1 && !l.variant_id;
    })) {
      setError("Select a variant for each variable product.");
      return;
    }
    setSaving(true);

    // One transactional call: the backend creates the header and every line
    // (simple, variation and bundle components) atomically, resolves the sellable
    // variant, generates the order number from a sequence, snapshots the
    // customer's billing/shipping address, and rejects on any invalid line — so
    // no partial sales order can ever be left behind.
    const pLines = lines.map((l) => {
      const prod = data?.products.find((p) => p.id === l.product_id);
      const unitPrice = Number(l.unit_price) || Number(prod?.selling_price) || 0;
      return {
        product_id: l.product_id,
        variant_id: l.variant_id ?? null,
        qty: Number(l.qty),
        unit_price: unitPrice,
      };
    });

    try {
      await salesApi.createSalesOrder({
        customerId,
        locationId,
        lines: pLines,
        source: "manual",
        status: "confirmed",
        currency: getActiveCurrencyCode(),
        priority,
        customerReference: customerReference || null,
        notes: notes || null,
        discount: Number(discount) || 0,
        tax: Number(tax) || 0,
        shipping: Number(shipping) || 0,
        idempotencyKey: createIdemKey,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }

    setSaving(false);

    setCreateOpen(false);
    setCustomerId("");
    setLocationId("");
    setLines([]);
    setPriority("normal");
    setCustomerReference("");
    setNotes("");
    setDiscount("");
    setTax("");
    setShipping("");
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const openFulfil = (so: SoRow) => {
    setFulfilSo(so);
    const items = (itemsBySo[so.id] ?? []).filter((it) => !it.is_bundle);
    const seeded = seedLineFulfilmentLocations({
      items,
      locations: data?.locations ?? [],
      balances: fulfilAvailability?.map ?? {},
      orderLocationId: so.location_id,
    });
    setFulfilQty(seeded.qtyByLineId);
    setFulfilLoc(seeded.locByLineId);
    setFulfilIdemKey(crypto.randomUUID());
    setError("");
  };

  useEffect(() => {
    if (!fulfilSo || !fulfilAvailabilityReady || !fulfilAvailability) return;
    const items = (itemsBySo[fulfilSo.id] ?? []).filter((it) => !it.is_bundle);
    const seeded = seedLineFulfilmentLocations({
      items,
      locations: fulfilAvailability.locations,
      balances: fulfilAvailability.map,
      orderLocationId: fulfilSo.location_id,
    });
    setFulfilLoc((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const [lineId, locationId] of Object.entries(seeded.locByLineId)) {
        if (!next[lineId]) {
          next[lineId] = locationId;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fill empty line locations once balances arrive
  }, [fulfilSo, fulfilAvailabilityReady, fulfilAvailability]);

  const handleFulfil = async () => {
    if (!fulfilSo) return;
    setError("");
    const items = (itemsBySo[fulfilSo.id] ?? []).filter((it) => !it.is_bundle);
    const built = buildFulfilmentRequest({
      items,
      qtyByLineId: fulfilQty,
      locByLineId: fulfilLoc,
      orderLocationId: fulfilSo.location_id,
      balances: fulfilAvailability?.map ?? {},
    });
    if (built.ok === false) {
      if (built.code === "no_qty") setError("Enter a quantity to fulfil for at least one line.");
      else if (built.code === "no_location") setError("Select a fulfilment location with available stock before confirming.");
      else setError("There isn't enough stock available to complete this. Check the quantities and try again.");
      return;
    }
    setSaving(true);
    try {
      await salesApi.fulfilSalesOrder({
        soId: fulfilSo.id,
        locationId: built.fallbackLocationId,
        lines: built.lines,
        idempotencyKey: fulfilIdemKey || undefined,
      });
    } catch (e) {
      setSaving(false);
      setError((e as Error).message);
      return;
    }
    setSaving(false);
    setFulfilSo(null);
    queryClient.invalidateQueries({ queryKey: ["sales-orders", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
  };

  const openCreate = () => {
    if (!canCreate) return;
    setError("");
    if ((data?.locations ?? []).length === 1 && !locationId) {
      setLocationId(data!.locations[0].id);
    }
    setCreateIdemKey(crypto.randomUUID());
    setCreateOpen(true);
  };

  useCreateDeepLink(openCreate, !!data);

  const handleExport = async () => {
    let q = supabase
      .from("sales_orders")
      .select("id, order_number, customer_id, location_id, source, status, order_date, total, customers(name), locations(name)")
      .eq("org_id", org!.id);
    const term = sanitize(search);
    if (term) {
      const ilike = `%${term}%`;
      const { data: matchedCustomers } = await supabase
        .from("customers")
        .select("id")
        .eq("org_id", org!.id)
        .ilike("name", ilike)
        .limit(100);
      const customerIds = ((matchedCustomers ?? []) as { id: string }[]).map((c) => c.id);
      if (customerIds.length) q = q.or(`order_number.ilike.${ilike},customer_id.in.(${customerIds.join(",")})`);
      else q = q.ilike("order_number", ilike);
    }
    if (statusFilter) q = q.eq("status", statusFilter);
    q = q.order("created_at", { ascending: false });
    const { data } = await q;
    const rows = (data ?? []).map((row) => ({
      ...row,
      customers: firstRelation(row.customers),
      locations: firstRelation(row.locations),
    })) as SoRow[];
    const headers = ["order_number", "customer", "location", "source", "status", "order_date", "total"];
    downloadCsv(
      `sales-orders-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(headers, rows.map((s) => [
        s.order_number,
        s.customers?.name ?? "",
        s.locations?.name ?? "",
        s.source,
        s.status,
        s.order_date ?? "",
        s.total,
      ])),
    );
  };

  const handlePrintSelected = async (kind: OrderDocumentKind) => {
    if (!org?.id || bulk.selectedIds.length === 0) return;
    setPrintError("");
    try {
      await printSalesOrderDocuments(org.id, bulk.selectedIds, kind);
    } catch (e) {
      setPrintError((e as Error).message);
    }
  };

  const handleExportSelected = () => {
    const rows = (data?.sos ?? []).filter((s) => bulk.selected.has(s.id));
    if (rows.length === 0) return;
    const headers = ["order_number", "customer", "location", "source", "status", "order_date", "total"];
    downloadCsv(
      `sales-orders-selected-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(headers, rows.map((s) => [
        s.order_number,
        s.customers?.name ?? "",
        s.locations?.name ?? "",
        s.source,
        s.status,
        s.order_date ?? "",
        s.total,
      ])),
    );
  };

  if (isLoading) return <ListSkeleton />;

  return (
    <div>
      <PageHeader title="Sales Orders" subtitle="Manage customer orders and fulfilment">
        <button onClick={handleExport} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
          <i className="ri-download-2-line" /> Export
        </button>
        {canCreate && (
          <button onClick={openCreate} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" />
            New Sales Order
          </button>
        )}
      </PageHeader>

      {isError ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-500 mb-4">Couldn't load sales orders.</p>
          <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
        </div>
      ) : !data || data.total === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200">
          <EmptyState
            icon="ri-file-list-line"
            title="No sales orders yet"
            description="Sales orders record what a customer is buying. Create your first one to begin the fulfilment journey."
            action={
              canCreate ? (
                <button onClick={openCreate} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
                  <i className="ri-add-line" /> Create your first order
                </button>
              ) : undefined
            }
          />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-md">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by order number or customer..."
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }} className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm">
              <option value="">All statuses</option>
              {ALL_SO_STATUSES.map((s) => <option key={s} value={s}>{soStatusLabel(s)}</option>)}
            </select>
            <span className="inline-flex items-center text-xs text-slate-400 whitespace-nowrap">{data?.total ?? 0} orders</span>
          </div>
          {printError && <div className="mx-4 mb-3 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{printError}</div>}
          {bulk.selectedIds.length > 0 && (
            <div className="px-4">
              <BulkActionBar count={bulk.selectedIds.length} onClear={bulk.clear}>
                {canPrint && <PrintDocumentsMenu mode="bulk" onPrint={(kind) => void handlePrintSelected(kind)} />}
                <button
                  type="button"
                  onClick={handleExportSelected}
                  className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                >
                  <i className="ri-download-2-line" /> Export
                </button>
              </BulkActionBar>
            </div>
          )}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/70 border-b border-slate-100">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={bulk.allSelected}
                      ref={(el) => { if (el) el.indeterminate = bulk.someSelected && !bulk.allSelected; }}
                      onChange={bulk.toggleAll}
                      aria-label="Select all orders on this page"
                      className="w-4 h-4 rounded accent-emerald-600"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Order</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Items</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.sos ?? []).length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-8 text-center text-sm text-slate-400">No sales orders match your search or filter.</td>
                  </tr>
                )}
                {data.sos.map((so) => {
                  const items = itemsBySo[so.id] ?? [];
                  const isOpen = expanded === so.id;
                  const canFulfil = FULFILLABLE_STATUSES.includes(so.status);
                  const promoteAllocate = canAllocateRole && shouldPromoteAllocateCta(so.status, items);
                  const showListFulfil = canFulfil && canFulfilRole;
                  const provenance = buildProvenance({ source: so.source, connectorProvider: so.connectors?.provider ?? null, connectorName: so.connectors?.name ?? null, externalOrderId: externalIdBySo[so.id] ?? null });
                  const afterSales = data.afterSalesBadges?.[so.id] ?? null;
                  return (
                    <Fragment key={so.id}>
                      <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => setExpanded(isOpen ? null : so.id)}>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={bulk.selected.has(so.id)}
                            onChange={() => bulk.toggle(so.id)}
                            aria-label={`Select ${so.order_number}`}
                            className="w-4 h-4 rounded accent-emerald-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/sales-orders/${so.id}`); }}
                            className="text-sm font-semibold text-slate-800 font-mono hover:text-emerald-700 whitespace-nowrap"
                          >
                            {so.order_number}
                          </button>
                          {afterSales && <div><SalesOrderListAfterSalesBadge badge={afterSales} /></div>}
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <SalesOrderItemSummaryView
                            summary={summarizeSalesOrderItems(items.map((it) => ({
                              productName: historicalProductName(it),
                              sku: historicalSku(it),
                              optionValues: historicalVariantOptions(it),
                              qty: it.qty_ordered,
                              bundleParentItemId: it.bundle_parent_item_id,
                            })))}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${provenance.isMarketplace ? "text-slate-600" : "text-slate-400"}`}>
                            <i className={provenance.isMarketplace ? "ri-store-2-line text-emerald-600" : "ri-file-list-3-line"} />
                            {provenanceStoreLabel(provenance)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">{so.customers?.name ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{so.locations?.name ?? "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{fmtDate(so.order_date)}</td>
                        <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">{money(so.total)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${paymentBadgeCls[paymentStatusTone(so.payment_status)]}`}>
                            {paymentStatusLabel(so.payment_status)}
                          </span>
                        </td>
                        <td className="px-4 py-3"><StatusBadge status={so.status} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {promoteAllocate && (
                              <button
                                onClick={(e) => { e.stopPropagation(); navigate(`/sales-orders/${so.id}`); }}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                              >
                                <i className="ri-lock-line" />
                                Allocate
                              </button>
                            )}
                            {showListFulfil && !promoteAllocate && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openFulfil(so); }}
                                className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                              >
                                <i className="ri-truck-line" />
                                Fulfil
                              </button>
                            )}
                            {showListFulfil && promoteAllocate && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openFulfil(so); }}
                                className="px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs font-medium rounded-lg whitespace-nowrap flex items-center gap-1"
                              >
                                Fulfil
                              </button>
                            )}
                            <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-slate-50 bg-slate-50/40">
                          <td colSpan={11} className="px-4 py-3">
                            <div className="divide-y divide-slate-100">
                              {items.map((it) => (
                                <div key={it.id} className="py-2 flex items-center justify-between text-sm">
                                  <div>
                                    <span className="font-medium text-slate-800">{historicalProductName(it)}</span>
                                    <span className="text-slate-400 font-mono text-xs ml-2">{historicalSku(it)}</span>
                                    {historicalVariantOptions(it) && (variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                                      <span className="text-slate-500 text-xs ml-2">{variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-6 text-slate-600">
                                    <span>{it.qty_fulfilled} / {it.qty_ordered} fulfilled</span>
                                    <span className="w-20 text-right">{money(it.unit_price)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-slate-100">
            {(data.sos ?? []).length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No sales orders match your search or filter.</div>
            ) : (
              data.sos.map((so) => (
                <SalesOrderCard
                  key={so.id}
                  order={so}
                  items={itemsBySo[so.id] ?? []}
                  externalOrderId={externalIdBySo[so.id] ?? null}
                  afterSalesBadge={data.afterSalesBadges?.[so.id] ?? null}
                  canFulfilRole={canFulfilRole}
                  canAllocateRole={canAllocateRole}
                  selected={bulk.selected.has(so.id)}
                  onToggleSelect={() => bulk.toggle(so.id)}
                  isOpen={expanded === so.id}
                  onToggle={() => setExpanded(expanded === so.id ? null : so.id)}
                  onFulfil={() => openFulfil(so)}
                  onAllocate={() => navigate(`/sales-orders/${so.id}`)}
                />
              ))
            )}
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onPageChange={setPage} onPageSizeChange={(s) => { setPageSize(s); setPage(0); }} />
        </div>
      )}

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New Sales Order" wide
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Creating..." : "Create Order"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreate} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Customer *</label>
              <select required value={customerId} onChange={(e) => setCustomerId(e.target.value)} className={inputCls}>
                <option value="">Select customer...</option>
                {data?.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Fulfilment location *</label>
              <select required value={locationId} onChange={(e) => setLocationId(e.target.value)} className={inputCls}>
                <option value="">Select location...</option>
                {data?.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-slate-500">Line items</label>
              <button type="button" onClick={addLine} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1 whitespace-nowrap">
                <i className="ri-add-line" /> Add line
              </button>
            </div>
            {lines.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg">
                No items yet. Click "Add line" to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {lines.map((l, idx) => {
                  const lineVariants = (data?.variants ?? []).filter((v) => v.product_id === l.product_id);
                  return (
                  <div key={l.uid} className="flex items-center gap-2">
                    <div className="flex-1 relative">
                    <select
                      value={l.product_id}
                      onChange={(e) => {
                        const prod = data?.products.find((p) => p.id === e.target.value);
                        const isBundle = prod?.product_type === "bundle";
                        const vs = (data?.variants ?? []).filter((v) => v.product_id === e.target.value);
                        updateLine(idx, { product_id: e.target.value, variant_id: vs.length === 1 ? vs[0].id : null, unit_price: String(Number(prod?.selling_price) || 0), is_bundle: isBundle });
                      }}
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="">Select product...</option>
                      {data?.products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku}){p.product_type === "bundle" ? " [Bundle]" : ""}</option>)}
                    </select>
                    {l.is_bundle && <span className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-semibold pointer-events-none">Bundle</span>}
                    </div>
                    {lineVariants.length === 1 ? (
                      <span className="w-32 px-3 py-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg truncate whitespace-nowrap">
                        {variantLabel(lineVariants[0].option_values) || lineVariants[0].sku || "Default"}
                      </span>
                    ) : lineVariants.length > 1 ? (
                      <select
                        value={l.variant_id ?? ""}
                        onChange={(e) => {
                          const vid = e.target.value || null;
                          const v = lineVariants.find((x) => x.id === vid);
                          updateLine(idx, { variant_id: vid, unit_price: v ? String(Number(v.selling_price)) : l.unit_price });
                        }}
                        className="w-32 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm"
                      >
                        <option value="">Select variant…</option>
                        {lineVariants.map((v) => <option key={v.id} value={v.id}>{variantLabel(v.option_values) || v.sku}</option>)}
                      </select>
                    ) : null}
                    <input type="number" min="1" value={l.qty} onChange={(e) => updateLine(idx, { qty: Number(e.target.value) })} className="w-20 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Qty" />
                    <input type="number" min="0" step="0.01" value={l.unit_price} onChange={(e) => updateLine(idx, { unit_price: e.target.value })} className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm" placeholder="Price" />
                    <button type="button" onClick={() => removeLine(idx)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500">
                      <i className="ri-delete-bin-line" />
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputCls}>
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Customer reference</label>
              <input type="text" value={customerReference} onChange={(e) => setCustomerReference(e.target.value)} placeholder="e.g. PO-1234" className={inputCls} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Discount</label>
              <input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Tax</label>
              <input type="number" min="0" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Shipping</label>
              <input type="number" min="0" step="0.01" value={shipping} onChange={(e) => setShipping(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputCls} placeholder="Internal notes..." />
          </div>

          <div className="flex items-center justify-end text-sm">
            <span className="text-slate-500 mr-2">Total ({getActiveCurrencyCode()}):</span>
            <span className="font-semibold text-slate-900">{money(grandTotal)}</span>
          </div>
        </form>
      </Modal>

      {fulfilSo && (
        <FulfilOrderModal
          open={!!fulfilSo}
          onClose={() => setFulfilSo(null)}
          orderNumber={fulfilSo.order_number}
          orderLocationId={fulfilSo.location_id}
          items={(itemsBySo[fulfilSo.id] ?? []).filter((it) => !it.is_bundle)}
          qtyByLineId={fulfilQty}
          locByLineId={fulfilLoc}
          onQtyChange={(lineId, qty) => setFulfilQty((q) => ({ ...q, [lineId]: qty }))}
          onLocChange={(lineId, locationId) => setFulfilLoc((l) => ({ ...l, [lineId]: locationId }))}
          locations={fulfilAvailability?.locations ?? data?.locations ?? []}
          balances={fulfilAvailability?.map ?? {}}
          error={error}
          saving={saving}
          confirmDisabled={fulfilmentConfirmDisabled({
            saving,
            availabilityReady: fulfilAvailabilityReady,
            items: (itemsBySo[fulfilSo.id] ?? []).filter((it) => !it.is_bundle),
            qtyByLineId: fulfilQty,
            locByLineId: fulfilLoc,
            orderLocationId: fulfilSo.location_id,
            balances: fulfilAvailability?.map ?? {},
          })}
          onConfirm={() => void handleFulfil()}
        />
      )}
    </div>
  );
}
